import { useState } from "react";
import axios from "axios";
import { CheckCircle, ArrowRight, CircleNotch } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const WaitlistForm = ({ variant = "light", idPrefix = "hero" }) => {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [message, setMessage] = useState("");

  // Both variants sit on dark evergreen/forest surfaces -> light text throughout.
  const labelColor = "text-champagne/70";
  const inputColor =
    "text-ivory placeholder:text-ivory/40 border-ivory/25 hover:border-gold focus:border-gold";

  const submit = async (e) => {
    e.preventDefault();
    if (!email || status === "loading") return;
    setStatus("loading");
    setMessage("");
    try {
      await axios.post(`${API}/waitlist`, {
        email,
        first_name: firstName || null,
      });
      setStatus("success");
      setMessage(
        firstName
          ? `Thank you, ${firstName}. You're on the list.`
          : "You're on the list. Watch your inbox."
      );
    } catch (err) {
      setStatus("error");
      const detail = err?.response?.data?.detail;
      setMessage(detail || "Something went wrong. Please try again.");
    }
  };

  if (status === "success") {
    return (
      <div
        data-testid={`${idPrefix}-waitlist-success`}
        className="flex items-start gap-4 py-6 animate-[float_1s_ease] max-w-md"
      >
        <span className="mt-1 shrink-0 flex h-11 w-11 items-center justify-center rounded-full bg-gold/15 border border-gold/40">
          <CheckCircle weight="thin" className="h-6 w-6 text-gold" />
        </span>
        <div>
          <p className="font-serif text-2xl text-ivory">{message}</p>
          <p className="mt-1 text-sm font-light text-champagne/70">
            Be among the first couples invited to experience GodTended.
          </p>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="w-full max-w-md" data-testid={`${idPrefix}-waitlist-form`}>
      <div className="flex flex-col gap-6">
        <div>
          <label className={`block text-xs uppercase tracking-[0.2em] mb-2 ${labelColor}`}>
            First Name <span className="lowercase tracking-normal opacity-60">(optional)</span>
          </label>
          <input
            data-testid={`${idPrefix}-waitlist-firstname-input`}
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="Your first name"
            className={`w-full bg-transparent border-b py-3 font-light outline-none rounded-none transition-colors ${inputColor}`}
          />
        </div>
        <div>
          <label className={`block text-xs uppercase tracking-[0.2em] mb-2 ${labelColor}`}>
            Email Address
          </label>
          <input
            data-testid={`${idPrefix}-waitlist-email-input`}
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className={`w-full bg-transparent border-b py-3 font-light outline-none rounded-none transition-colors ${inputColor}`}
          />
        </div>

        <button
          data-testid={`${idPrefix}-waitlist-submit-button`}
          type="submit"
          disabled={status === "loading"}
          className="group mt-2 inline-flex items-center justify-center gap-3 rounded-full bg-gold px-8 py-4 font-medium tracking-wide text-evergreen shadow-[0_8px_30px_rgba(201,161,93,0.35)] transition-all duration-500 ease-lux hover:bg-champagne hover:-translate-y-1 disabled:opacity-70 disabled:hover:translate-y-0"
        >
          {status === "loading" ? (
            <CircleNotch weight="bold" className="h-5 w-5 animate-spin" />
          ) : (
            <>
              Notify Me First
              <ArrowRight weight="light" className="h-5 w-5 transition-transform duration-500 ease-lux group-hover:translate-x-1" />
            </>
          )}
        </button>

        {status === "error" && (
          <p data-testid={`${idPrefix}-waitlist-error`} className="text-sm text-[#e0a06f] font-light">
            {message}
          </p>
        )}

        <p className="text-sm font-light text-champagne/60">
          Be among the first couples invited to experience GodTended.{" "}
          <span className="text-gold">No spam. Ever.</span>
        </p>
      </div>
    </form>
  );
};
